import { Card, Link } from "@heroui/react"
import type { SelfOrderStorefront } from "@ramoo/contracts"
import { tamu } from "@ramoo/i18n/tamu"
import { warnaTeksGelap } from "@ramoo/self-order-ui"
import { useEffect } from "react"
import { teksDiAtas, warnaSah } from "./warna"

/**
 * Etalase merchant: sampul, spanduk, dan kaki halaman — bagian yang membuat halaman ini terlihat
 * seperti toko yang bersangkutan, bukan seperti Ramoo.
 *
 * Semua isinya boleh kosong dan halaman tetap benar. Itu bukan kemewahan: sebagian besar merchant
 * tidak akan pernah membuka layar pengaturannya, dan self-order yang cuma jalan setelah diisi
 * berarti self-order yang tidak jalan.
 */

/**
 * Warna utama etalase menimpa aksen design system di akar halaman, bukan sebagai `style` per
 * elemen.
 *
 * Alasannya bukan kerapian: aksen dipakai belasan tempat (tombol, harga, chip aktif, sampul,
 * cincin fokus), dan menaruhnya di tiap elemen berarti ada elemen yang terlewat — biasanya yang
 * paling jarang dilihat saat menguji. Satu titik pasang, semua ikut, termasuk lembar detail yang
 * dirender di luar akar aplikasi. Turunan aksennya (hover, soft, fokus) ada di styles.css.
 *
 * Warna aksen kedua etalase (`accentColor`) tidak dipasang: design system hanya punya satu aksen,
 * dan cincin fokus yang dulu memakainya sekarang mengikuti warna utama.
 *
 * Versi teks untuk tema gelap (`--etalase-aksen-gelap`) selalu ikut dipasang, karena tema bisa
 * berganti tanpa muat ulang saat setelan HP tamu berubah.
 */
export const useWarnaMerek = (s: SelfOrderStorefront | undefined) => {
  const warna = s?.primaryColor
  useEffect(() => {
    if (!warnaSah(warna)) return
    const akar = document.documentElement
    akar.style.setProperty("--etalase-aksen", warna)
    akar.style.setProperty("--etalase-aksen-gelap", warnaTeksGelap(warna))
    akar.dataset.etalase = teksDiAtas(warna)
    return () => {
      akar.style.removeProperty("--etalase-aksen")
      akar.style.removeProperty("--etalase-aksen-gelap")
      delete akar.dataset.etalase
    }
  }, [warna])
}

export function Sampul({
  storefront,
  namaOutlet,
}: {
  storefront: SelfOrderStorefront
  namaOutlet: string
}) {
  if (!storefront.showHero) return null
  const judul = storefront.heroTitle ?? storefront.displayName ?? namaOutlet
  /* Tanpa sampul yang diisi DAN tanpa gambar, sampul cuma mengulang nama toko yang sudah ada di
     kepala halaman — satu layar penuh untuk tidak mengatakan apa-apa. Lebih baik langsung menu. */
  if (!storefront.heroImageUrl && !storefront.heroTitle && !storefront.heroSubtitle) return null
  return (
    <section className="relative bg-accent text-accent-foreground">
      {storefront.heroImageUrl ? (
        <img
          className="block h-44 w-full object-cover opacity-55"
          src={storefront.heroImageUrl}
          alt=""
        />
      ) : null}
      <div
        className={
          storefront.heroImageUrl
            ? "absolute inset-x-0 bottom-0 bg-linear-to-b from-transparent to-backdrop p-4"
            : "p-4"
        }
      >
        {storefront.logoUrl ? (
          <img className="mb-2 h-10 w-auto rounded-lg" src={storefront.logoUrl} alt={judul} />
        ) : null}
        <h1 className="text-2xl leading-8 font-semibold">{judul}</h1>
        {storefront.heroSubtitle ? (
          <p className="mt-1 text-sm opacity-90">{storefront.heroSubtitle}</p>
        ) : null}
      </div>
    </section>
  )
}

/**
 * Rel spanduk: gulir mendatar dengan scroll-snap. Padanan `carousel` Pro dengan paket gratis;
 * tanpa titik penanda dan tombol panah, sama seperti sebelumnya.
 */
export function Spanduk({ storefront }: { storefront: SelfOrderStorefront }) {
  if (!storefront.showBanners || !storefront.banners.length) return null
  return (
    <section className="flex snap-x snap-mandatory scroll-px-4 scrollbar-none gap-3 overflow-x-auto px-4 pt-3 pb-2">
      {storefront.banners.map((b) => {
        const isi = (
          <>
            {b.imageUrl ? (
              <img className="block h-28 w-full object-cover" src={b.imageUrl} alt="" />
            ) : null}
            <Card.Content className="p-3">
              <Card.Title>{b.title}</Card.Title>
              {b.description ? <Card.Description>{b.description}</Card.Description> : null}
            </Card.Content>
          </>
        )
        const kelas = "shrink-0 basis-4/5 snap-start gap-0 overflow-hidden p-0"
        /* `rel="noreferrer"` untuk tautan keluar yang diisi merchant: halaman tujuan tak perlu tahu
           dari etalase mana tamunya datang, dan `noopener` menutup akses balik ke jendela ini. */
        return b.linkUrl ? (
          <Card<"a">
            key={b.id}
            className={kelas}
            href={b.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            render={(props) => <a {...props} />}
          >
            {isi}
          </Card>
        ) : (
          <Card key={b.id} className={kelas}>
            {isi}
          </Card>
        )
      })}
    </section>
  )
}

export function TierLoyalty({ storefront }: { storefront: SelfOrderStorefront }) {
  if (!storefront.loyalty?.tiers.length) return null
  return (
    <section className="flex flex-col gap-2 px-4 pt-3">
      <div>
        <h2 className="text-lg leading-7 font-semibold">Keuntungan member</h2>
        <p className="text-xs text-muted">Belanja dan jumlah pesanan harus memenuhi syarat tier.</p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(--spacing(36),1fr))] gap-2">
        {storefront.loyalty.tiers.map((tier) => (
          <Card<"article">
            key={tier.id}
            className="gap-1 p-3"
            render={(props) => <article {...props} />}
          >
            <Card.Title>{tier.name}</Card.Title>
            <Card.Description className="text-xs">
              Mulai Rp{tier.minSpent.toLocaleString("id-ID")} · {tier.minOrders} pesanan
            </Card.Description>
            {tier.memberDiscountPercent > 0 ? (
              <strong className="text-sm font-semibold text-aksen-teks">
                Diskon {tier.memberDiscountPercent}%
              </strong>
            ) : null}
          </Card>
        ))}
      </div>
    </section>
  )
}

export function KakiEtalase({
  storefront,
  outletId,
}: {
  storefront: SelfOrderStorefront
  outletId: string
}) {
  const sosial = [
    { url: storefront.instagramUrl, label: "Instagram" },
    { url: storefront.facebookUrl, label: "Facebook" },
    { url: storefront.tiktokUrl, label: "TikTok" },
    {
      url: storefront.whatsappNumber
        ? `https://wa.me/${storefront.whatsappNumber.replace(/[^\d]/g, "")}`
        : null,
      label: "WhatsApp",
    },
  ].filter((x): x is { url: string; label: string } => !!x.url)
  /* Tautan privasi selalu ada (Modul 13 N6): pelanggan berhak meminta datanya di toko mana pun,
     termasuk toko yang tidak mengisi kaki etalase. */
  return (
    <footer className="flex flex-col items-center gap-2 px-4 pt-6 pb-2 text-center text-sm text-muted">
      {storefront.footerText ? <p>{storefront.footerText}</p> : null}
      {sosial.length ? (
        <div className="flex flex-wrap justify-center gap-4">
          {sosial.map((x) => (
            <Link
              key={x.label}
              className="font-semibold text-aksen-teks"
              href={x.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {x.label}
            </Link>
          ))}
        </div>
      ) : null}
      <Link className="text-sm text-muted" href={`/privasi/${encodeURIComponent(outletId)}`}>
        {tamu().privasiTautan}
      </Link>
    </footer>
  )
}
